'use client';

import { useCallback } from 'react';
import Clarity from '@microsoft/clarity';

/**
 * Hook for manually tracking Clarity events and setting tags.
 *
 * Use this when you want to track specific user interactions
 * that aren't automatically captured.
 *
 * @example
 * const { trackEvent, setTag } = useClarity();
 *
 * // Track a custom event
 * trackEvent('checkout_started');
 *
 * // Set a custom tag
 * setTag('plan_type', 'premium');
 */
export function useClarity() {
  const trackEvent = useCallback((name: string) => {
    if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID) {
      return;
    }

    try {
      Clarity.event(name);
    } catch (err) {
      console.debug('[Clarity] Failed to track event:', err);
    }
  }, []);

  const setTag = useCallback((key: string, value: string | string[]) => {
    if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID) {
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (err) {
      console.debug('[Clarity] Failed to set tag:', err);
    }
  }, []);

  return { trackEvent, setTag };
}
